import React from "react";
import { Pause, Play } from "lucide-react";
import type { CronometragemItem } from "../../../services/controleProducaoService";

export function formatTempoMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
}

export function cronometroPausadoBloqueiaLancamento(
  cronometragem: CronometragemItem | null | undefined
): boolean {
  return cronometragem?.status === "PAUSED";
}

export const MIN_TEMPO_LANCAR_PRODUCAO_MS = 60_000;

function epochMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export function motivoBloqueioLancamentoProducao(input: {
  status?: string | null;
  tempoExibidoMs: number;
  jaProduziu?: boolean;
}): string | null {
  if (input.jaProduziu) return null;
  const status = input.status ?? "";
  if (status === "FINISHED") return null;
  if (status === "PAUSED") {
    return "Retome o cronômetro para lançar a produção.";
  }
  if (status !== "RUNNING") {
    return "Inicie o cronômetro para lançar a produção.";
  }
  if (input.tempoExibidoMs < MIN_TEMPO_LANCAR_PRODUCAO_MS) {
    const faltaSeg = Math.max(
      1,
      Math.ceil((MIN_TEMPO_LANCAR_PRODUCAO_MS - input.tempoExibidoMs) / 1000)
    );
    const mm = Math.floor(faltaSeg / 60);
    const ss = String(faltaSeg % 60).padStart(2, "0");
    return `Aguarde ${mm}:${ss} no cronômetro para lançar a quantidade.`;
  }
  return null;
}

/** Recalcula o tempo a partir das marcações do servidor (SSOT). `nowMs` é Date.now(). */
export function cronometragemComTick(
  cronometragem: CronometragemItem | null | undefined,
  nowMs: number
): CronometragemItem | null {
  if (!cronometragem) return null;
  if (cronometragem.status === "RUNNING") {
    const sinceMs = epochMs(cronometragem.runningSince);
    const tempoLiquidoMs =
      sinceMs == null
        ? cronometragem.tempoLiquidoMs
        : cronometragem.tempoLiquidoMs + Math.max(0, nowMs - sinceMs);
    return {
      ...cronometragem,
      tempoLiquidoMs,
      tempoFormatado: formatTempoMs(tempoLiquidoMs),
    };
  }
  if (cronometragem.status === "PAUSED") {
    const pausedMs = epochMs(cronometragem.pausedAt);
    const basePausa = cronometragem.tempoPausaManualMs ?? 0;
    const tempoPausaManualMs =
      pausedMs == null ? basePausa : basePausa + Math.max(0, nowMs - pausedMs);
    return {
      ...cronometragem,
      tempoPausaManualMs,
      tempoPausaFormatado: formatTempoMs(tempoPausaManualMs),
    };
  }
  return cronometragem;
}

type CronometroEstado = "idle" | "running" | "paused" | "finished";

function estadoDoCronometro(
  status: string | undefined,
  pausado: boolean
): CronometroEstado {
  if (status === "FINISHED") return "finished";
  if (status === "RUNNING") return "running";
  if (pausado) return "paused";
  return "idle";
}

function classesTouchCrono(estado: CronometroEstado): string {
  if (estado === "running") {
    return "border-red-500 bg-red-50 animate-forno-observation-pulse";
  }
  if (estado === "paused") {
    return "border-orange-400 bg-orange-50 animate-crono-pause-pulse";
  }
  if (estado === "finished") {
    return "border-emerald-500 bg-emerald-50";
  }
  return "border-neutral-200 bg-white";
}

function corTempoCrono(estado: CronometroEstado): string {
  if (estado === "running") return "text-red-700";
  if (estado === "paused") return "text-orange-800";
  if (estado === "finished") return "text-emerald-800";
  return "text-neutral-900";
}

type CronometroItemButtonsProps = {
  cronometragem?: CronometragemItem | null;
  disabled?: boolean;
  somenteLeitura?: boolean;
  variant?: "compact" | "touch";
  onIniciar: () => void;
  onPausar: () => void;
  onRetomar: () => void;
};

const CronometroItemButtons: React.FC<CronometroItemButtonsProps> = ({
  cronometragem,
  disabled,
  somenteLeitura = false,
  variant = "compact",
  onIniciar,
  onPausar,
  onRetomar,
}) => {
  const status = cronometragem?.status;
  const tempo = cronometragem?.tempoFormatado || "00:00";
  const tempoPausa = cronometragem?.tempoPausaFormatado
    || formatTempoMs(cronometragem?.tempoPausaManualMs ?? 0);
  const mostraPausa =
    (cronometragem?.tempoPausaManualMs ?? 0) > 0 || status === "PAUSED";
  const touch = variant === "touch";
  const icon = touch ? "h-3.5 w-3.5" : "h-4 w-4";
  const circle = touch
    ? "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white shadow-sm touch-manipulation disabled:opacity-50"
    : "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white touch-manipulation disabled:opacity-50";

  const pausado = status === "PAUSED";
  const estado = estadoDoCronometro(status, pausado);
  const labelStatusLeitura =
    estado === "finished"
      ? "Encerrado"
      : estado === "running"
        ? "Produzindo"
        : estado === "paused"
          ? "Pausado"
          : null;

  const badgePausa = mostraPausa ? (
    <span className="mt-0.5 inline-flex rounded-md bg-orange-200 px-2 py-0.5 font-mono text-xs font-bold tabular-nums leading-none text-orange-950">
      Pausa {tempoPausa}
    </span>
  ) : null;

  const painelTouch = (conteudoAcao: React.ReactNode) => (
    <div
      className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${classesTouchCrono(estado)}`}
    >
      <div className="min-w-0 flex-1">
        <p
          className={`text-[9px] font-semibold uppercase tracking-wide ${
            estado === "running"
              ? "text-red-700"
              : estado === "paused"
                ? "text-orange-800"
                : estado === "finished"
                  ? "text-emerald-800"
                  : "text-neutral-500"
          }`}
        >
          Cronômetro
        </p>
        <p
          className={`font-mono text-lg font-bold tabular-nums leading-none tracking-tight ${corTempoCrono(estado)}`}
        >
          {tempo}
        </p>
        {badgePausa}
      </div>
      {conteudoAcao}
    </div>
  );

  if (somenteLeitura) {
    if (!cronometragem) {
      return (
        <span className="text-xs font-medium text-neutral-500">—</span>
      );
    }
    if (touch) {
      return painelTouch(
        labelStatusLeitura ? (
          <span
            className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold ${
              estado === "finished"
                ? "bg-emerald-200 text-emerald-900"
                : estado === "running"
                  ? "bg-red-200 text-red-900"
                  : "bg-orange-200 text-orange-900"
            }`}
          >
            {labelStatusLeitura}
          </span>
        ) : null
      );
    }
    return (
      <div className="flex flex-col items-center gap-1">
        <span className={`font-mono text-base font-bold tabular-nums leading-none tracking-tight ${corTempoCrono(estado)}`}>
          {tempo}
        </span>
        {labelStatusLeitura ? (
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              estado === "finished"
                ? "bg-emerald-100 text-emerald-800"
                : estado === "running"
                  ? "bg-red-100 text-red-800"
                  : "bg-orange-100 text-orange-800"
            }`}
          >
            {labelStatusLeitura}
          </span>
        ) : null}
        {mostraPausa && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-mono text-[10px] font-semibold tabular-nums text-amber-800">
            Pausa {tempoPausa}
          </span>
        )}
      </div>
    );
  }

  const actions = (
    <>
      {status !== "FINISHED" && status !== "RUNNING" && (
        <button
          type="button"
          disabled={disabled}
          onClick={pausado ? onRetomar : onIniciar}
          aria-label={pausado ? "Retomar cronômetro" : "Iniciar cronômetro"}
          title={pausado ? "Retomar" : "Iniciar"}
          className={`${circle} ${
            pausado
              ? "bg-orange-500 hover:bg-orange-600"
              : "bg-emerald-600 hover:bg-emerald-700"
          }`}
        >
          <Play className={`${icon} fill-current`} />
        </button>
      )}
      {status === "RUNNING" && (
        <button
          type="button"
          disabled={disabled}
          onClick={onPausar}
          aria-label="Pausar cronômetro"
          title="Pausar"
          className={`${circle} bg-red-600 hover:bg-red-700`}
        >
          <Pause className={`${icon} fill-current`} />
        </button>
      )}
    </>
  );

  if (touch) {
    return painelTouch(
      estado === "finished" ? (
        <span className="shrink-0 rounded-full bg-emerald-200 px-2 py-1 text-[11px] font-semibold text-emerald-900">
          Encerrado
        </span>
      ) : (
        <div className="flex shrink-0 items-center">{actions}</div>
      )
    );
  }

  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className={`font-mono text-base font-bold tabular-nums leading-none tracking-tight ${corTempoCrono(estado)}`}>
        {tempo}
      </span>
      {status === "FINISHED" ? (
        <div className="flex flex-col items-center gap-1">
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
            Encerrado
          </span>
          {mostraPausa && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 font-mono text-[10px] font-semibold tabular-nums text-amber-800">
              Pausa {tempoPausa}
            </span>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1">
          <div className="flex items-center justify-center gap-1.5">{actions}</div>
          {status === "RUNNING" && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800">
              Produzindo
            </span>
          )}
          {pausado && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 font-mono text-[10px] font-semibold tabular-nums text-amber-800">
              {mostraPausa ? `Pausa ${tempoPausa}` : "Pausado"}
            </span>
          )}
          {!pausado && mostraPausa && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 font-mono text-[10px] font-semibold tabular-nums text-amber-800">
              Pausa {tempoPausa}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default CronometroItemButtons;
